import { Directive, ElementRef, HostListener, Input, AfterViewInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { PHONE_FORMAT_CONSTANTS, PHONE_REGEX_PATTERNS } from '../utility/constants';

interface CountryPhoneFormat {
  country: string;
  format: string;
  length: number | string;
  example: string;
}

@Directive({
  selector: '[appPhoneFormat]',
  standalone: true
})
export class PhoneFormatDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input() selectedCountryCode: string = '';

  private valueChangesSubscription?: Subscription;
  private statusChangesSubscription?: Subscription;
  private isUserInput = false;
  private readonly allowedCharactersRegex = PHONE_REGEX_PATTERNS.ALLOWED_CHARACTERS;
  private readonly extractDigitsRegex = PHONE_REGEX_PATTERNS.EXTRACT_DIGITS;
  private readonly extractAlphanumericRegex = PHONE_REGEX_PATTERNS.EXTRACT_ALPHANUMERIC;
  private readonly removeInvalidCharsRegex = PHONE_REGEX_PATTERNS.REMOVE_INVALID_CHARS;
  private readonly formattingCharsRegex = PHONE_REGEX_PATTERNS.FORMATTING_CHARS;
  private readonly supportedCountryFormats: CountryPhoneFormat[] = [
    { country: "United States", format: "(XXX) XXX-XXXX", length: 10, example: "(123) 456-7890" },
    { country: "Canada", format: "(XXX) XXX-XXXX", length: 10, example: "(416) 123-4567" },
    { country: "United Kingdom", format: "XXX XXXX XXXX", length: "10, 11", example: "020 1234 5678 or 20 1234 5678" },
    { country: "Italy", format: "XX XXXX XXXX", length: 10, example: "02 1234 5678" },
    { country: "Germany", format: "XXX XXXXXXXX", length: 11, example: "030 12345678" },
    { country: "France", format: "XX XX XX XX XX", length: 10, example: "01 23 45 67 89" },
    { country: "Spain", format: "XXX XXX XXX", length: 9, example: "912 345 678" },
    { country: "Australia", format: "XX XXXX XXXX", length: 10, example: "02 1234 5678" },
    { country: "India", format: "XXXXX XXXXX", length: 10, example: "98765 43210" },
    { country: "Japan", format: "XX-XXXX-XXXX", length: 10, example: "03-1234-5678" },
    { country: "South Korea", format: "XX-XXXX-XXXX", length: 10, example: "02-1234-5678" },
    { country: "China", format: "XXX-XXXX-XXXX", length: 11, example: "138-1234-5678" },
    { country: "Malaysia", format: "XX-XXXX XXXX", length: 10, example: "03-1234 5678" },
    { country: "New Zealand", format: "XX-XXX XXXX", length: 9, example: "09-123 4567" },
    { country: "Philippines", format: "XX XXX XXXXX", length: 10, example: "02 123 45678" },
    { country: "Thailand", format: "XX-XXX-XXXX", length: 9, example: "02-123-4567" },
    { country: "Singapore", format: "XXXX XXXX", length: 8, example: "9123 4567" },
    { country: "Indonesia", format: "XXX-XXXX-XXXX", length: 11, example: "021-1234-5678" },
    { country: "Vietnam", format: "XXX XXX XXXXX", length: 11, example: "024 123 45678" },
    { country: "United Arab Emirates", format: "XXX XXX XXXX", length: 10, example: "050 123 4567" },
    { country: "Saudi Arabia", format: "XXX XXX XXXX", length: 10, example: "050 123 4567" },
    { country: "Israel", format: "XX-XXX-XXXX", length: 9, example: "02-123-4567" },
    { country: "South Africa", format: "XXX XXX XXXX", length: 10, example: "011 123 4567" },
    { country: "Nigeria", format: "XXXX XXX XXXX", length: 11, example: "0123 456 7890" },
    { country: "Egypt", format: "XX XXXX XXXX", length: 10, example: "02 1234 5678" },
    { country: "Brazil", format: "(XX) XXXXX-XXXX", length: 11, example: "(11) 98765-4321" },
    { country: "Mexico", format: "(XX) XXXX-XXXX", length: 10, example: "(55) 1234-5678" },
    { country: "Argentina", format: "(XXX) XX-XXXX-XX", length: 11, example: "(011) 12-3456-78" },
    { country: "Chile", format: "(XX) XXXX XXXX", length: 10, example: "(02) 1234 5678" },
    { country: "Colombia", format: "(XX) XXX-XXXX", length: 10, example: "(03) 123-4567" },
    { country: "Netherlands", format: "XXX XXX XXXX", length: 10, example: "020 123 4567" },
    { country: "Turkey", format: "XXXX XXX XX XX", length: 11, example: "0212 123 45 67" },
    { country: "Poland", format: "XXX XXX XXX", length: 9, example: "123 456 789" },
    { country: "Czech Republic", format: "XXX XXX XXX", length: 9, example: "123 456 789" },
    { country: "Sweden", format: "XX-XXX XX XX", length: 9, example: "08-123 45 67" },
    { country: "Norway", format: "XXX XX XXX", length: 8, example: "123 45 678" },
    { country: "Switzerland", format: "XXX XXX XX XX", length: 10, example: "021 234 56 78" },
    { country: "Belgium", format: "XXX XX XX XX", length: 9, example: "021 23 45 67" },
    { country: "Austria", format: "XXXX XXXXXX", length: 10, example: "0123 456789" },
    { country: "Denmark", format: "XX XX XX XX", length: 8, example: "12 34 56 78" },
    { country: "Finland", format: "XX XXX XXXX", length: 9, example: "09 123 4567" },
    { country: "Ireland", format: "XX XXX XXXX", length: 9, example: "01 234 5678" },
    { country: "Greece", format: "XXX XXX XXXX", length: 10, example: "212 345 6789" },
    { country: "Portugal", format: "XXX XXX XXX", length: 9, example: "212 345 678" },
    { country: "Russia", format: "(XXX) XXX-XX-XX", length: 10, example: "(123) 456-78-90" },
    { country: "Pakistan", format: "XXX XXX XXXX", length: 10, example: "921 123 4567" },
    { country: "Bangladesh", format: "XXX XXX XXXX", length: 10, example: "978 123 4567" },
    { country: "Sri Lanka", format: "XX XXX XXXX", length: 10, example: "01 123 4567" },
    { country: "Hong Kong", format: "XXXX XXXX", length: 8, example: "1234 5678" },
    { country: "Peru", format: "(XX) XXX-XXXX", length: 9, example: "(01) 123-4567" },
    { country: "Venezuela", format: "(XXX) XXX-XXXX", length: 11, example: "(212) 123-4567" },
    { country: "Kenya", format: "XXX XXX XXXX", length: 10, example: "020 123 4567" }
  ];
  private readonly countryCodeToNameMap: { [key: string]: string } = {
    'US': 'United States',
    'CA': 'Canada', 
    'GB': 'United Kingdom',
    'IT': 'Italy',
    'DE': 'Germany',
    'FR': 'France',
    'ES': 'Spain',
    'AU': 'Australia',
    'IN': 'India',
    'JP': 'Japan',
    'KR': 'South Korea',
    'CN': 'China',
    'MY': 'Malaysia',
    'NZ': 'New Zealand',
    'PH': 'Philippines',
    'TH': 'Thailand',
    'SG': 'Singapore',
    'ID': 'Indonesia',
    'VN': 'Vietnam',
    'AE': 'United Arab Emirates',
    'SA': 'Saudi Arabia',
    'IL': 'Israel',
    'ZA': 'South Africa',
    'NG': 'Nigeria',
    'EG': 'Egypt',
    'BR': 'Brazil',
    'MX': 'Mexico',
    'AR': 'Argentina',
    'CL': 'Chile',
    'CO': 'Colombia',
    'NL': 'Netherlands',
    'TR': 'Turkey',
    'PL': 'Poland',
    'CZ': 'Czech Republic',
    'SE': 'Sweden',
    'NO': 'Norway',
    'CH': 'Switzerland',
    'BE': 'Belgium',
    'AT': 'Austria',
    'DK': 'Denmark',
    'FI': 'Finland',
    'IE': 'Ireland',
    'GR': 'Greece',
    'PT': 'Portugal',
    'RU': 'Russia',
    'PK': 'Pakistan',
    'BD': 'Bangladesh',
    'LK': 'Sri Lanka',
    'HK': 'Hong Kong',
    'PE': 'Peru',
    'VE': 'Venezuela',
    'KE': 'Kenya'
  };

  constructor(
    private readonly elementRef: ElementRef,
    private readonly formControl: NgControl
  ) {}

  ngAfterViewInit() {
    if (this.formControl.control) {
      this.statusChangesSubscription =
        this.formControl.control.statusChanges.subscribe(() => {
          setTimeout(() => {
            this.revalidateIfNeeded();
          }, PHONE_FORMAT_CONSTANTS.REVALIDATION_DELAY);
        });

      this.valueChangesSubscription = this.formControl.control.valueChanges.subscribe((value: string) => {
        if (!this.isUserInput && value && typeof value === 'string') {
          setTimeout(() => {
            this.formatPhoneOnProgrammaticChange(value);
          }, 0);
        }
        this.isUserInput = false;
      });
    }
  }

  ngOnDestroy() {
    this.statusChangesSubscription?.unsubscribe();
    this.valueChangesSubscription?.unsubscribe();
  }

  private formatPhoneOnProgrammaticChange(value: string): void {
    if (!this.formControl.control || !this.elementRef.nativeElement) {
      return;
    }

    const sanitizedValue = this.sanitizeInput(value);
    if (!sanitizedValue) {
      return;
    }

    const formattedValue = this.formatPhoneNumber(sanitizedValue);
    
    if (formattedValue && formattedValue !== value) {
      this.formControl.control.setValue(formattedValue, { emitEvent: false });
      this.elementRef.nativeElement.value = formattedValue;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedCountryCode'] && !changes['selectedCountryCode'].firstChange) {
      this.reformatPhoneOnCountryChange();
    }
  }

  private revalidateIfNeeded(): void {
    if (this.formControl.control && this.elementRef.nativeElement.value) {
      const currentInputValue = this.elementRef.nativeElement.value;
      const sanitizedValue = this.sanitizeInput(currentInputValue);
      const validationErrors = this.validatePhoneInput(sanitizedValue);
      
      if (validationErrors) {
        const existingErrors = this.formControl.control.errors;
        const hasPhoneValidationError = existingErrors && 
          (existingErrors[PHONE_FORMAT_CONSTANTS.ERROR_TYPES.PHONE_MIN_LENGTH] || 
           existingErrors[PHONE_FORMAT_CONSTANTS.ERROR_TYPES.PHONE_MAX_LENGTH]);
        
        if (!hasPhoneValidationError) {
          this.setValidationError(validationErrors);
        }
      }
    }
  }

  private reformatPhoneOnCountryChange(): void {
    if (this.formControl.control && this.elementRef.nativeElement.value) {
      const currentInputValue = this.elementRef.nativeElement.value;
      const sanitizedValue = this.sanitizeInput(currentInputValue);
      const formattedValue = this.formatPhoneNumber(sanitizedValue);
      
      if (this.elementRef.nativeElement.value !== formattedValue) {
        this.elementRef.nativeElement.value = formattedValue;
        this.formControl.control.setValue(formattedValue);
      }
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: any): void {
    this.isUserInput = true;
    const inputElement = event.target;
    const inputValue = inputElement.value;
    
    const sanitizedValue = this.sanitizeInput(inputValue);
    
    if (!this.isValidInputLength(sanitizedValue)) {
      const previousValue = this.formControl.value || '';
      inputElement.value = previousValue;
      return;
    }

    const formattedValue = this.formatPhoneNumber(sanitizedValue);
    
    if (inputElement.value !== formattedValue) {
      inputElement.value = formattedValue;
      this.formControl.control?.setValue(formattedValue);
    }

    if (this.formControl.control) {
      this.formControl.control.markAsTouched();
      
      const validationErrors = this.validatePhoneInput(sanitizedValue);
      if (validationErrors) {
        this.setValidationError(validationErrors);
      } else {
        this.clearPhoneValidationErrors();
      }
    }
  }

  @HostListener('keypress', ['$event'])
  onKeyPress(event: KeyboardEvent): void {
    const pressedKey = event.key;
    
    if (this.isControlKey(event)) {
      return;
    }

    if (!this.allowedCharactersRegex.test(pressedKey)) {
      event.preventDefault();
      return;
    }

    const currentInputValue = this.elementRef.nativeElement.value;
    const projectedValue = currentInputValue + pressedKey;
    
    if (!this.isValidInputLength(this.sanitizeInput(projectedValue))) {
      event.preventDefault();
    }
  }

  @HostListener('blur', ['$event'])
  onBlur(event: any): void {
    const inputValue = event.target.value;
    const sanitizedValue = this.sanitizeInput(inputValue);
    
    if (this.formControl.control) {
      const formattedValue = this.formatPhoneNumber(sanitizedValue);
      event.target.value = formattedValue;
      
      this.formControl.control.markAsTouched();
      
      const validationErrors = this.validatePhoneInput(sanitizedValue);
      if (validationErrors) {
        this.setValidationError(validationErrors);
      } else {
        this.clearPhoneValidationErrors();
      }
      
      this.formControl.control.setValue(formattedValue);
    }
  }

  private sanitizeInput(inputValue: string): string {
    return inputValue.replace(this.removeInvalidCharsRegex, '');
  }

  private isValidInputLength(sanitizedValue: string): boolean {
    const digitsOnly = sanitizedValue.replace(this.extractDigitsRegex, '');
    const alphanumericOnly = sanitizedValue.replace(this.extractAlphanumericRegex, '');
    
    if (digitsOnly === sanitizedValue.replace(this.formattingCharsRegex, '')) {
      return digitsOnly.length <= PHONE_FORMAT_CONSTANTS.VALIDATION_LIMITS.DIGITS_MAX;
    } else {
      return alphanumericOnly.length <= PHONE_FORMAT_CONSTANTS.VALIDATION_LIMITS.ALPHANUMERIC_MAX;
    }
  }

  private isControlKey(event: KeyboardEvent): boolean {
    return PHONE_FORMAT_CONSTANTS.CONTROL_KEYS.includes(event.key) ||
           event.ctrlKey || 
           event.metaKey;
  }

  private formatPhoneNumber(sanitizedValue: string): string {
    const digitsOnly = sanitizedValue.replace(this.extractDigitsRegex, '');
    
    if (!digitsOnly || !this.selectedCountryCode) {
      return sanitizedValue;
    }

    const countryName = this.countryCodeToNameMap[this.selectedCountryCode];
    if (!countryName) {
      return sanitizedValue;
    }

    const countryPhoneFormat = this.supportedCountryFormats.find(
      formatConfig => formatConfig.country === countryName
    );

    if (!countryPhoneFormat) {
      return sanitizedValue;
    }

    const acceptedLengths = this.parseValidLengths(countryPhoneFormat.length);
    
    if (acceptedLengths.includes(digitsOnly.length)) {
      let formatPattern = countryPhoneFormat.format;
      
      if (countryName === "United Kingdom") {
        if (digitsOnly.length === 10) {
          formatPattern = "XX XXXX XXXX";
        } else if (digitsOnly.length === 11) {
          formatPattern = "XXX XXXX XXXX";
        }
      }
      
      return this.applyPhoneFormat(digitsOnly, formatPattern);
    }

    return sanitizedValue;
  }

  private parseValidLengths(lengthConfig: number | string): number[] {
    if (typeof lengthConfig === 'number') {
      return [lengthConfig];
    }
    
    return lengthConfig.split(',').map(lengthStr => Number.parseInt(lengthStr.trim(), 10));
  }

  private applyPhoneFormat(digitsOnly: string, formatPattern: string): string {
    let formattedPhoneNumber = '';
    let currentDigitIndex = 0;

    for (let i = 0; i < formatPattern.length && currentDigitIndex < digitsOnly.length; i++) {
      if (formatPattern[i] === 'X') {
        formattedPhoneNumber += digitsOnly[currentDigitIndex];
        currentDigitIndex++;
      } else {
        formattedPhoneNumber += formatPattern[i];
      }
    }

    return formattedPhoneNumber;
  }


  private validatePhoneInput(sanitizedValue: string) {
    if (!sanitizedValue) {
      return null;
    }

    const digitsOnly = sanitizedValue.replace(this.extractDigitsRegex, '');
    const alphanumericOnly = sanitizedValue.replace(this.extractAlphanumericRegex, '');
    
    const isDigitsOnlyInput = digitsOnly === sanitizedValue.replace(this.formattingCharsRegex, '');
    
    if (isDigitsOnlyInput) {
      if (digitsOnly.length < PHONE_FORMAT_CONSTANTS.VALIDATION_LIMITS.DIGITS_MIN) {
        return { 
          [PHONE_FORMAT_CONSTANTS.ERROR_TYPES.PHONE_MIN_LENGTH]: { 
            requiredLength: PHONE_FORMAT_CONSTANTS.VALIDATION_LIMITS.DIGITS_MIN, 
            actualLength: digitsOnly.length, 
            isDigitsOnly: true 
          } 
        };
      }
      if (digitsOnly.length > PHONE_FORMAT_CONSTANTS.VALIDATION_LIMITS.DIGITS_MAX) {
        return { 
          [PHONE_FORMAT_CONSTANTS.ERROR_TYPES.PHONE_MAX_LENGTH]: { 
            requiredLength: PHONE_FORMAT_CONSTANTS.VALIDATION_LIMITS.DIGITS_MAX, 
            actualLength: digitsOnly.length, 
            isDigitsOnly: true 
          } 
        };
      }
    } else {
      if (alphanumericOnly.length < PHONE_FORMAT_CONSTANTS.VALIDATION_LIMITS.ALPHANUMERIC_MIN) {
        return { 
          [PHONE_FORMAT_CONSTANTS.ERROR_TYPES.PHONE_MIN_LENGTH]: { 
            requiredLength: PHONE_FORMAT_CONSTANTS.VALIDATION_LIMITS.ALPHANUMERIC_MIN, 
            actualLength: alphanumericOnly.length, 
            isDigitsOnly: false 
          } 
        };
      }
      if (alphanumericOnly.length > PHONE_FORMAT_CONSTANTS.VALIDATION_LIMITS.ALPHANUMERIC_MAX) {
        return { 
          [PHONE_FORMAT_CONSTANTS.ERROR_TYPES.PHONE_MAX_LENGTH]: { 
            requiredLength: PHONE_FORMAT_CONSTANTS.VALIDATION_LIMITS.ALPHANUMERIC_MAX, 
            actualLength: alphanumericOnly.length, 
            isDigitsOnly: false 
          } 
        };
      }
    }

    return null;
  }

  private setValidationError(validationErrors: any): void {
    if (this.formControl.control) {
      const existingErrors = this.formControl.control.errors || {};
      const updatedErrors = { ...existingErrors, ...validationErrors };
      this.formControl.control.setErrors(updatedErrors);
    }
  }

  private clearPhoneValidationErrors(): void {
    if (this.formControl.control) {
      const existingErrors = this.formControl.control.errors;
      if (existingErrors) {
        delete existingErrors[PHONE_FORMAT_CONSTANTS.ERROR_TYPES.PHONE_MIN_LENGTH];
        delete existingErrors[PHONE_FORMAT_CONSTANTS.ERROR_TYPES.PHONE_MAX_LENGTH];
        const hasRemainingErrors = Object.keys(existingErrors).length > 0;
        this.formControl.control.setErrors(hasRemainingErrors ? existingErrors : null);
      }
    }
  }
}
